/**
 * Calendar View — monthly grid showing shoot days
 * Each day shows: Day #, primary location, scene count, page count, cast
 * Color-coded by INT/EXT ratio
 */
import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Film } from 'lucide-react';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Kerala monsoon months — flag EXT-heavy days
const MONSOON_MONTHS = [5, 6, 7, 8, 9]; // Jun–Oct (0-indexed)

export default function CalendarView({ days, allScenes, breakdownById }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  // Map ISO date → shoot day(s)
  const daysByDate = useMemo(() => {
    const map = {};
    days.forEach(d => {
      if (!d.shoot_date) return;
      if (!map[d.shoot_date]) map[d.shoot_date] = [];
      map[d.shoot_date].push(d);
    });
    return map;
  }, [days]);

  // Build calendar grid for this month
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const isMonsoon = MONSOON_MONTHS.includes(month);

  const iso = (d) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={prevMonth}
            className="h-8 w-8 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center hover:border-[#e8c547]/40 text-[#a0a0a0] hover:text-[#f0f0f0] transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0]">
            {MONTH_NAMES[month]} {year}
          </h2>
          <button
            onClick={nextMonth}
            className="h-8 w-8 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center hover:border-[#e8c547]/40 text-[#a0a0a0] hover:text-[#f0f0f0] transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {isMonsoon && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs">
            🌧 Monsoon month — prioritise INT scenes
          </div>
        )}
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1">
        {DAY_LABELS.map(l => (
          <div key={l} className="text-center text-[10px] font-semibold text-[#555] py-1 uppercase tracking-wider">
            {l}
          </div>
        ))}

        {/* Calendar cells */}
        {cells.map((day, idx) => {
          if (!day) return <div key={`pad-${idx}`} />;
          const dateStr = iso(day);
          const shootDays = daysByDate[dateStr] || [];
          const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
          const hasShoot = shootDays.length > 0;

          // Compute stats
          let totalPages = 0;
          let sceneCount = 0;
          let extCount = 0;
          const allCast = new Set();
          const allLocs = new Set();

          shootDays.forEach(sd => {
            (sd.scene_ids || []).forEach(sid => {
              const s = breakdownById[sid];
              if (!s) return;
              sceneCount++;
              totalPages += (s.page_count_eighths || 0) / 8;
              if ((s.location_type || '').toUpperCase() === 'EXT') extCount++;
              if (s.location_name) allLocs.add(s.location_name);
              (s.characters_in_scene || []).forEach(c => allCast.add(c));
            });
          });

          // Cell color based on INT/EXT ratio
          let cellAccent = '#2a2a2a';
          if (hasShoot) {
            const extRatio = sceneCount > 0 ? extCount / sceneCount : 0;
            if (extRatio > 0.6) cellAccent = '#0ea5e920'; // mostly EXT → blue tint
            else if (extRatio < 0.4) cellAccent = '#e8c54715'; // mostly INT → warm tint
            else cellAccent = '#44bb8815'; // mixed → green tint
          }

          return (
            <div
              key={dateStr}
              className={`
                rounded-lg p-2 min-h-[80px] border transition-all
                ${isToday ? 'border-[#e8c547] ring-1 ring-[#e8c547]/30' : 'border-[#1e1e1e]'}
                ${hasShoot ? 'cursor-pointer hover:border-[#e8c547]/40' : ''}
              `}
              style={{ backgroundColor: hasShoot ? cellAccent : 'transparent' }}
              data-testid={`cal-cell-${dateStr}`}
            >
              {/* Date number */}
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold ${isToday ? 'text-[#e8c547]' : hasShoot ? 'text-[#f0f0f0]' : 'text-[#444]'}`}>
                  {day}
                </span>
                {hasShoot && shootDays[0] && (
                  <span className="text-[9px] font-bold text-[#e8c547] bg-[#e8c547]/10 px-1 rounded">
                    D{shootDays[0].day_number}
                  </span>
                )}
              </div>

              {/* Shoot info */}
              {hasShoot && (
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1">
                    <Film className="h-2.5 w-2.5 text-[#a0a0a0]" />
                    <span className="text-[10px] text-[#a0a0a0]">{sceneCount}sc · {totalPages.toFixed(1)}p</span>
                  </div>
                  {allLocs.size > 0 && (
                    <div className="text-[9px] text-[#666] truncate">
                      {Array.from(allLocs).slice(0, 2).join(', ')}
                    </div>
                  )}
                  {allCast.size > 0 && (
                    <div className="text-[9px] text-[#555] truncate">
                      {Array.from(allCast).slice(0, 3).map(n => n.split(' ')[0]).join(', ')}
                    </div>
                  )}
                  {/* INT/EXT bar */}
                  {sceneCount > 0 && (
                    <div className="h-1 rounded-full bg-[#1a1a1a] overflow-hidden mt-1">
                      <div
                        className="h-full bg-sky-500 rounded-full"
                        style={{ width: `${(extCount / sceneCount) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-[#555] border-t border-[#1e1e1e] pt-3">
        <div className="flex items-center gap-1.5">
          <div className="w-8 h-1.5 rounded-full bg-sky-500" />
          <span>EXT ratio (blue bar)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-sky-500/15 border border-sky-500/20" />
          <span>Mostly EXT</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#e8c547]/15 border border-[#e8c547]/20" />
          <span>Mostly INT</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500/15 border border-emerald-500/20" />
          <span>Mixed</span>
        </div>
      </div>
    </div>
  );
}
